import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        // Case 1: Emergent Google Auth returns #session_id=... in the hash
        const hash = window.location.hash || "";
        const sessMatch = hash.match(/session_id=([^&]+)/);
        if (sessMatch) {
          const { data } = await api.post("/auth/google/exchange", { session_id: sessMatch[1] });
          setToken(data.token);
          setUser(data.user);
          window.history.replaceState({}, document.title, window.location.pathname);
          nav("/app", { replace: true });
          return;
        }

        // Case 2: Generic OAuth providers return ?code=&state=&provider=
        const params = new URLSearchParams(loc.search);
        const code = params.get("code");
        const state = params.get("state");
        const provider = params.get("provider") || sessionStorage.getItem("oauth_provider");
        if (code && state && provider) {
          const redirectUri = `${window.location.origin}/auth/callback?provider=${encodeURIComponent(provider)}`;
          const { data } = await api.post(`/auth/${provider}/callback`, null, { params: { code, state, redirect_uri: redirectUri } });
          setToken(data.token);
          setUser(data.user);
          sessionStorage.removeItem("oauth_provider");
          nav("/app", { replace: true });
          return;
        }

        setError("Missing session information");
      } catch (e) {
        setError(e.response?.data?.detail || "Sign-in failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground" data-testid="auth-callback-page">
      <div className="card-flat p-10 text-center">
        {!error ? (
          <>
            <Loader2 size={48} className="animate-spin mx-auto text-primary"/>
            <div className="mt-6 font-display font-bold text-2xl tracking-tight">Signing you in...</div>
            <div className="text-sm text-muted-foreground mt-2">One moment.</div>
          </>
        ) : (
          <div className="text-red-400">
            <div className="font-display font-bold text-xl mb-2">Sign-in failed</div>
            <div className="text-sm text-muted-foreground mb-4">{error}</div>
            <button onClick={()=>nav("/login")} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-2.5">Back to login</button>
          </div>
        )}
      </div>
    </div>
  );
}
